import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Printer, Coins, TrendingUp, Building2, Briefcase, Wallet, HelpCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { DatePicker } from "@/components/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const Card = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-6">
    <div className={`h-10 w-10 rounded-md flex items-center justify-center mb-4 ${accent}`}>
      <Icon className="h-5 w-5" />
    </div>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-3xl font-display font-semibold mt-1 tabular-nums">{value}</p>
  </div>
);

const TOOLTIP_STYLE = { background: "#0A192F", border: "none", borderRadius: 6, color: "white" };
const BAR_RADIUS = [6, 6, 0, 0];

export default function Commissions() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthly, setMonthly] = useState(null);
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    const [s, m] = await Promise.all([
      api.get("/commissions/summary"),
      api.get("/commissions/monthly", { params: { year } }),
    ]);
    setData(s.data);
    setMonthly(m.data);
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const filteredCompanies = useMemo(() => {
    if (!data?.by_company) return [];
    const q = search.trim().toLowerCase();
    return data.by_company.filter(
      (c) => (companyFilter === "all" || c.name === companyFilter) && (!q || c.name.toLowerCase().includes(q)),
    );
  }, [data, search, companyFilter]);

  if (!data || !monthly) return <div className="text-slate-400">Caricamento…</div>;

  const chart = monthly.months.map((m) => ({
    name: m.label,
    Provvigioni: m.commission,
    Costi: m.cost,
    Netto: m.net,
  }));

  return (
    <div className="space-y-8 animate-fade-up" data-testid="commissions-page">
      <header className="flex items-end justify-between flex-wrap gap-4 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Provvigioni</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3"><Coins className="h-8 w-8 sm:h-10 sm:w-10 text-rose-600 bg-rose-100 rounded-lg p-1.5" />Conteggio provvigioni</h1>
          <p className="text-slate-500 mt-1">Provvigioni ricevute, costi sostenuti e netto.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            data-testid="open-commission-help"
            onClick={() => setShowHelp(true)}
            title="Guida: come si calcolano provvigioni e fee"
          >
            <HelpCircle className="h-4 w-4 mr-2" /> Come si calcola?
          </Button>
          <Button variant="outline" data-testid="goto-statement" onClick={() => window.location.assign("/commissions/statement")}>
            📊 Estratto conto
          </Button>
          <Button
            className="bg-pink-600 hover:bg-pink-700 text-white border-0"
            data-testid="goto-broker-report"
            onClick={() => window.location.assign("/commissions/broker-report")}
          >
            🏆 Report Broker
          </Button>
          <Button variant="outline" data-testid="print-commissions" onClick={() => window.print()}>
            <Printer className="h-4 w-4 mr-2" /> Stampa
          </Button>
        </div>
      </header>

      <div className="hidden print:block mb-6">
        <h1 className="font-display text-2xl font-semibold">Rendiconto Provvigioni — Assicura</h1>
        <p className="text-xs text-slate-500">{new Date().toLocaleDateString("it-IT")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card icon={Coins} label="Premi totali" value={formatCurrency(data.total_premium)} accent="bg-blue-50 text-blue-700" testid="kpi-premium" />
        <Card icon={TrendingUp} label="Provvigioni ricevute" value={formatCurrency(data.total_commission)} accent="bg-emerald-50 text-emerald-700" testid="kpi-commission" />
        <Card icon={Wallet} label="Costo commissioni" value={formatCurrency(data.total_commission_cost)} accent="bg-rose-50 text-rose-700" testid="kpi-cost" />
        <Card icon={TrendingUp} label="Netto" value={formatCurrency(data.total_net)} accent="bg-amber-50 text-amber-700" testid="kpi-net" />
        <Card icon={Building2} label="Compagnie" value={data.by_company.length} accent="bg-slate-100 text-slate-700" testid="kpi-companies" />
      </div>

      <section>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3 print:hidden">
          <div>
            <h2 className="font-display text-xl font-semibold">Andamento mensile</h2>
            <p className="text-sm text-slate-500">Per anno solare, in base alla decorrenza polizza.</p>
          </div>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger data-testid="year-select" className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 3 + i).map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="h-72" data-testid="monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="Provvigioni" fill="#2563EB" radius={BAR_RADIUS} />
                <Bar dataKey="Costi" fill="#F43F5E" radius={BAR_RADIUS} />
                <Bar dataKey="Netto" fill="#10B981" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-semibold mb-3">Sintesi mensile {year}</h2>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-rose-600 text-white text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Mese</th>
                <th className="text-right px-4 py-3">Polizze</th>
                <th className="text-right px-4 py-3">Premi</th>
                <th className="text-right px-4 py-3">Provvigioni</th>
                <th className="text-right px-4 py-3">Costo</th>
                <th className="text-right px-4 py-3">Netto</th>
                <th className="text-left px-4 py-3 print:hidden">Dettaglio</th>
              </tr>
            </thead>
            <tbody data-testid="monthly-table">
              {monthly.months.map((m) => (
                <tr key={m.month} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium">{m.label}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-500">{m.count}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(m.premium)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">{formatCurrency(m.commission)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-rose-700">{formatCurrency(m.cost)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{formatCurrency(m.net)}</td>
                  <td className="px-4 py-3 print:hidden text-xs text-slate-500">
                    {m.by_company.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {m.by_company.map((c) => (
                          <span key={c.name} className="bg-slate-100 rounded px-1.5 py-0.5" title={`Premi ${formatCurrency(c.premium)} · Comm ${formatCurrency(c.commission)}`}>
                            {c.name}: {formatCurrency(c.commission)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3 print:hidden">
          <h2 className="font-display text-xl font-semibold">Totale per compagnia</h2>
          <div className="flex gap-2 items-center">
            <Input
              data-testid="commissions-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca compagnia…"
              className="w-56 text-sm"
            />
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger data-testid="commissions-company-filter" className="w-48"><SelectValue placeholder="Tutte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le compagnie</SelectItem>
                {data.by_company.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-rose-600 text-white text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-6 py-3">Compagnia</th>
                <th className="text-right px-6 py-3">Polizze</th>
                <th className="text-right px-6 py-3">Premi</th>
                <th className="text-right px-6 py-3">Provvigione</th>
                <th className="text-right px-6 py-3">Costo</th>
                <th className="text-right px-6 py-3">Netto</th>
              </tr>
            </thead>
            <tbody data-testid="commissions-companies">
              {filteredCompanies.map((c) => (
                <tr key={c.name} className="border-t border-slate-100">
                  <td className="px-6 py-4 font-medium">{c.name}</td>
                  <td className="px-6 py-4 text-right tabular-nums">{c.count}</td>
                  <td className="px-6 py-4 text-right tabular-nums">{formatCurrency(c.premium)}</td>
                  <td className="px-6 py-4 text-right tabular-nums text-emerald-700">{formatCurrency(c.commission)}</td>
                  <td className="px-6 py-4 text-right tabular-nums text-rose-700">{formatCurrency(c.cost)}</td>
                  <td className="px-6 py-4 text-right tabular-nums font-semibold">{formatCurrency(c.net)}</td>
                </tr>
              ))}
              {filteredCompanies.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400">Nessun dato.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-semibold mb-3">Totale per broker / agenzia</h2>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-rose-600 text-white text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-6 py-3">Partner</th>
                <th className="text-right px-6 py-3">Polizze</th>
                <th className="text-right px-6 py-3">Premi</th>
                <th className="text-right px-6 py-3">Provvigione</th>
                <th className="text-right px-6 py-3">Costo</th>
                <th className="text-right px-6 py-3">Netto</th>
              </tr>
            </thead>
            <tbody data-testid="commissions-brokers">
              {data.by_broker.map((b) => (
                <tr key={b.name} className="border-t border-slate-100">
                  <td className="px-6 py-4 font-medium">{b.name}</td>
                  <td className="px-6 py-4 text-right tabular-nums">{b.count}</td>
                  <td className="px-6 py-4 text-right tabular-nums">{formatCurrency(b.premium)}</td>
                  <td className="px-6 py-4 text-right tabular-nums text-emerald-700">{formatCurrency(b.commission)}</td>
                  <td className="px-6 py-4 text-right tabular-nums text-rose-700">{formatCurrency(b.cost)}</td>
                  <td className="px-6 py-4 text-right tabular-nums font-semibold">{formatCurrency(b.net)}</td>
                </tr>
              ))}
              {data.by_broker.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400">Nessun broker collegato alle polizze.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <BreakdownSection />

      {/* Dialog: guida rapida al calcolo provvigioni + fee */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="commission-help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-blue-600" />
              Come si calcolano provvigioni e fee
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 text-sm text-slate-700">
            <section>
              <h3 className="font-semibold text-slate-900 mb-2">📥 Campi input dalla polizza</h3>
              <ul className="ml-5 list-disc space-y-1">
                <li><b>Base imponibile</b>: <code>net_premium</code> → <code>taxable_base</code> → <code>premium</code> (fallback in cascata)</li>
                <li><b>Fee (diritto)</b>: <code>commission_cost</code> — importo <b>fisso in €</b>, indipendente dalla provvigione</li>
                <li><b>Ramo</b>: derivato da <code>type</code> (auto, moto, casa, vita, RC, ecc.)</li>
                <li><b>Data d&apos;incasso</b>: <code>paid_at</code> / <code>paid_date</code> — filtro periodo del Report Broker</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-2">🎯 Priorità delle percentuali (cascata)</h3>
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs space-y-2">
                <div>
                  <b>% provvigione ramo:</b><br/>
                  <span className="text-slate-600">link Broker↔Compagnia (<code>branch_rates</code>) → default compagnia (<code>default_branch_rates</code>) → 0</span>
                </div>
                <div>
                  <b>% retrocessione al collaboratore:</b><br/>
                  <span className="text-slate-600">link (<code>collab_commission_share_percent</code>) → anagrafica collaboratore → 0</span>
                </div>
                <div>
                  <b>Modalità + valori fee al collaboratore:</b><br/>
                  <span className="text-slate-600">link (<code>collab_extra_share_*</code>) → anagrafica collaboratore → <code>none</code></span>
                </div>
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-2">🧮 Formule step-by-step</h3>
              <div className="rounded border border-blue-200 bg-blue-50 p-3 text-xs font-mono leading-6 whitespace-pre-wrap">{`commission_total  = net_premium × pct_ramo / 100
collab_commission = commission_total × collab_pct / 100
broker_commission = commission_total − collab_commission

fee_total = policy.commission_cost   (importo fisso €)

fee_collab = 0                        se mode = "none"
           = fee_total × fee_pct/100  se mode = "percent"
           = min(fixed_amt, fee_total) se mode = "fixed"
fee_broker = fee_total − fee_collab

agency_total = broker_commission + fee_broker    ← all'agenzia
collab_total = collab_commission + fee_collab    ← al collaboratore`}</div>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-2">⚠️ Punti chiave</h3>
              <ul className="ml-5 list-disc space-y-1">
                <li>Provvigione e Fee sono <b>due flussi separati</b> — il totale broker è la loro <b>somma</b>.</li>
                <li>La fee <b>NON</b> si somma al premio lordo e <b>NON</b> viene scorporata dalla provvigione.</li>
                <li>La <b>base della provvigione</b> è sempre il <b>netto</b> (net_premium), non il lordo.</li>
                <li>Se <code>fixed_amount</code> supera <code>fee_total</code>, viene applicato un <b>cap</b> al valore della fee stessa.</li>
                <li>Il Report Broker filtra per <b>data d&apos;incasso</b> (non per decorrenza).</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-2">💡 Esempio numerico</h3>
              <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs font-mono leading-5 whitespace-pre-wrap">{`Polizza RC Auto:
  net_premium         = 800 €
  commission_cost     =  30 €   (fee)
  link.branch_rates[auto] = 15 %
  collab_commission_share_percent = 40 %
  collab_extra_share_mode = "fixed", fixed_amount = 10 €

→ commission_total    = 800 × 15 % = 120,00 €
→ collab_commission   = 120 × 40 % =  48,00 €
→ broker_commission   = 120 − 48   =  72,00 €

→ fee_total           = 30,00 €
→ fee_collab          = min(10, 30) = 10,00 €
→ fee_broker          = 30 − 10   = 20,00 €

→ agency_total = 72 + 20 =  92,00 € ← al broker/agenzia
→ collab_total = 48 + 10 =  58,00 € ← al collaboratore`}</div>
            </section>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHelp(false)} data-testid="close-commission-help">
              Ho capito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BreakdownSection() {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(firstDay);
  const [dateTo, setDateTo] = useState(lastDay);
  const [paidOnly, setPaidOnly] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/commissions/breakdown", { params: { date_from: dateFrom, date_to: dateTo, paid_only: paidOnly } });
      setReport(data);
    } finally { setLoading(false); }
  }, [dateFrom, dateTo, paidOnly]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/commissions/breakdown", {
        params: { date_from: dateFrom, date_to: dateTo, paid_only: paidOnly, format: "csv" },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = paidOnly ? "_solo-incassate" : "";
      a.download = `report-provvigioni_${dateFrom}_${dateTo}${suffix}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  return (
    <section data-testid="commissions-breakdown-section" className="space-y-3 mt-10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold">Report Provvigioni — Partner ↔ Compagnia ↔ Ramo</h2>
          <p className="text-xs text-slate-500 mt-0.5">{paidOnly ? "Solo polizze incassate (cashflow reale)" : "Tutte le polizze (provvigioni maturate)"}</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-700 h-9 px-3 bg-white border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50" data-testid="breakdown-paid-only-wrap">
            <input
              type="checkbox"
              checked={paidOnly}
              onChange={(e) => setPaidOnly(e.target.checked)}
              data-testid="breakdown-paid-only"
              className="accent-pink-600"
            />
            Solo incassate
          </label>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 block mb-1">Dal</label>
            <DatePicker data-testid="breakdown-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 block mb-1">Al</label>
            <DatePicker data-testid="breakdown-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" />
          </div>
          <Button data-testid="breakdown-refresh" onClick={fetchReport} disabled={loading} className="bg-pink-600 hover:bg-pink-700 h-9">
            {loading ? "…" : "Aggiorna"}
          </Button>
          <Button
            data-testid="breakdown-export-csv"
            onClick={exportCsv}
            variant="outline"
            className="border-pink-300 text-pink-700 hover:bg-pink-50 h-9"
            title="Esporta il report in CSV (compatibile Excel)"
          >
            📊 Esporta CSV
          </Button>
        </div>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="breakdown-totals">
            <div className="bg-white border border-slate-200 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Polizze</p>
              <p className="font-display font-semibold text-xl tabular-nums">{report.totals.policies_count}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Premio lordo</p>
              <p className="font-display font-semibold text-xl tabular-nums">{formatCurrency(report.totals.premium)}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wider text-emerald-700">Imponibile</p>
              <p className="font-display font-semibold text-xl tabular-nums text-emerald-800">{formatCurrency(report.totals.imponibile)}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wider text-blue-700">Provvigioni</p>
              <p className="font-display font-semibold text-xl tabular-nums text-blue-800">{formatCurrency(report.totals.commission)}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">rate effettivo {report.totals.effective_rate}%</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wider text-amber-700">Extra fees</p>
              <p className="font-display font-semibold text-xl tabular-nums text-amber-800">{formatCurrency(report.totals.extra_fees)}</p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-white text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Partner</th>
                  <th className="text-left px-4 py-3">Compagnia</th>
                  <th className="text-left px-4 py-3">Ramo</th>
                  <th className="text-right px-4 py-3">Polizze</th>
                  <th className="text-right px-4 py-3">Imponibile</th>
                  <th className="text-right px-4 py-3">Provvigione</th>
                  <th className="text-right px-4 py-3">Rate eff.</th>
                  <th className="text-right px-4 py-3">Extra fees</th>
                </tr>
              </thead>
              <tbody data-testid="breakdown-rows">
                {report.rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`breakdown-row-${i}`}>
                    <td className="px-4 py-3">
                      {r.broker_name}
                      {r.broker_type && <span className="text-[10px] uppercase text-slate-400 ml-1">({r.broker_type})</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{r.company_name}</td>
                    <td className="px-4 py-3"><span className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{r.ramo_label}</span></td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.policies_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-800 font-medium">{formatCurrency(r.imponibile)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-blue-800 font-semibold">{formatCurrency(r.commission)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-500 text-xs">{r.effective_rate}%</td>
                    <td className="px-4 py-3 text-right tabular-nums text-amber-800">{formatCurrency(r.extra_fees)}</td>
                  </tr>
                ))}
                {report.rows.length === 0 && (
                  <tr><td colSpan="8" className="text-center py-8 text-slate-400">Nessuna polizza nel periodo selezionato.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
